import { useCallback, useEffect, useRef, useState } from 'react';
import { getApartments } from '../api.apartments';
import { getToken } from '../auth';
import { deriveSetupStep, SETUP_STATE_CHANGED_EVENT, SetupStep } from '../utils/setupStep';

/**
 * Next setup step for the nav cue, or null (logged out, ADMIN, finished, not loaded, or failed).
 *
 * Re-fetch policy: a finished account must not pay a request per navigation, so we fetch on mount
 * and when the user changes, re-fetch on a route change only while still onboarding (the manager
 * is likely to have just done the step), and always re-fetch on SETUP_STATE_CHANGED_EVENT so that
 * deleting the last apartment or source brings the cue back at once.
 */
export function useSetupStep(userEmail: string | null, userRole: string | null, pathname: string): SetupStep | null {
  const [step, setStep] = useState<SetupStep | null>(null);
  const stepRef = useRef<SetupStep | null>(null);
  const seq = useRef(0);
  const active = !!userEmail && userRole !== 'ADMIN';

  const apply = useCallback((next: SetupStep | null) => {
    stepRef.current = next;
    setStep(next);
  }, []);

  const load = useCallback(() => {
    // Only the newest response may win; bumping seq on cleanup also stops setState after unmount.
    const id = ++seq.current;
    getApartments(getToken())
      .then((data) => { if (id === seq.current) apply(deriveSetupStep(data)); })
      .catch(() => { if (id === seq.current) apply(null); });
  }, [apply]);

  useEffect(() => {
    if (!active) {
      apply(null);
      return;
    }
    // A different account must never see the previous one's step while its own request is in flight.
    apply(null);
    load();
    // seq is a counter, not a DOM ref: the cleanup deliberately bumps the live value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { seq.current++; };
  }, [active, userEmail, load, apply]);

  useEffect(() => {
    if (active && stepRef.current) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!active) return;
    window.addEventListener(SETUP_STATE_CHANGED_EVENT, load);
    return () => window.removeEventListener(SETUP_STATE_CHANGED_EVENT, load);
  }, [active, load]);

  return active ? step : null;
}
